import * as React from 'react';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box } from '@material-ui/core';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { globalEventsChannel } from '../lib/events';
import { Row } from './layout/Row';
import { Column } from './layout/Column';
import { useQueryParams } from './utils/hooks';
import { useGlobalAppState } from '../context/GlobalAppStateContext';
import { SET_USER_TEAM } from '../mutation/team';

const TEAM_SWITCH_TIMEOUT = 10000;

interface TeamCheckWrapperProps {
  teamId?: string
  dataTeamId?: string
  requireReload?: boolean
}

export const InvalidTeamMessage = () => {
  const { t } = useTranslation();

  return (
    <div className="central-message">
      <Paper elevation={12}>
        <Box p={3}>
          <Row>
            <Column md={2}>
              <Box height="100%" display="flex" justifyContent="center">
                <HourglassEmptyIcon fontSize="large" />
              </Box>
            </Column>

            <Column md={10}>
              <h3>{t('global.invalidTeam.title', 'Requested data is from another team')}</h3>
              <p>{t('global.invalidTeam.text', 'Please wait, changing team selection')}</p>
            </Column>
          </Row>
        </Box>
      </Paper>
    </div>
  );
};

const TeamCheckWrapper: React.FC<TeamCheckWrapperProps & {children: JSX.Element}> = (props) => {
  const {
    children,
    dataTeamId,
    teamId,
    requireReload,
  } = props;

  const [queryParams, setQueryParams] = useQueryParams();
  const { teamIsChanging, setTeamIsChanging } = useGlobalAppState();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [switchGaveUp, setSwitchGaveUp] = useState(false);

  const [setTeam] = useMutation(SET_USER_TEAM, {
    onError: async (e) => {
      console.error('Team change refused by this server', e);
      setTeamIsChanging(false);
      setSwitchGaveUp(true);
    },
    onCompleted: async () => {
      setTeamIsChanging(false);
    },
  });

  const comparedTeamId = dataTeamId || queryParams.teamId;
  const mismatch = !!comparedTeamId && !!teamId && teamId !== comparedTeamId;
  const dropTeamParam = !dataTeamId && switchGaveUp;

  const attempted = useRef<string | null>(null);

  const updateTeam = useCallback((tId: string, forceReload?: boolean) => {
    if (teamIsChanging) {
      return;
    }
    setTeamIsChanging(tId);

    enqueueSnackbar(t('teams.dialog.teamChanged', 'Data from another team. Changing team.'), {
      variant: 'warning',
      autoHideDuration: 5000,
      id: `team-change-${tId}`,
      preventDuplicate: true,
    });
    setTeam({
      variables: {
        teamId: tId,
      },
    });

    setTimeout(() => {
      if (requireReload || forceReload) {
        window.location.reload();
      }
    }, 3000);
  }, [teamIsChanging]);

  useEffect(() => {
    // console.log('teamIsChanging')
    const unsubscribeTeamChange = globalEventsChannel.on('onTeamChange', (tId, forceReload) => {
      if (!teamIsChanging) {
        // console.log('event - onTeamChange', tId, teamIsChanging)
        updateTeam(tId, forceReload);
      } else {
        // console.log('event - onTeamChange _ TEAM IS CHANGING', tId, teamIsChanging)
      }
    });
    return () => {
      unsubscribeTeamChange();
      // setTeamIsChanging(false);
    };
  }, [teamIsChanging]);

  useEffect(() => {
    if (teamIsChanging || switchGaveUp || !mismatch) {
      return;
    }
    if (attempted.current === comparedTeamId) {
      return;
    }
    attempted.current = comparedTeamId;
    globalEventsChannel.emit('onTeamChange', comparedTeamId);
  }, [comparedTeamId, teamId, teamIsChanging, mismatch, switchGaveUp]);

  useEffect(() => {
    if (!teamIsChanging) {
      return undefined;
    }
    const timer = setTimeout(() => setSwitchGaveUp(true), TEAM_SWITCH_TIMEOUT);
    return () => clearTimeout(timer);
  }, [teamIsChanging]);

  useEffect(() => {
    if (!dropTeamParam || !queryParams.teamId) {
      return;
    }
    const { teamId: unreachable, ...rest } = queryParams;
    setQueryParams(rest, { replace: true });
  }, [dropTeamParam]);

  return mismatch && !switchGaveUp
    ? <InvalidTeamMessage />
    : children;
};

export default TeamCheckWrapper;
